import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, BadgeCheck, CircleDollarSign, ShieldCheck, MailCheck } from "lucide-react";

const KIND_ICONS = {
  membership: ShieldCheck,
  verification: BadgeCheck,
  payment: CircleDollarSign,
};

export const NotificationsPanel = ({ notifications, onRefresh }) => {
  const unread = notifications.filter((n) => !n.is_read).length;

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read");
      onRefresh?.();
      toast.success("Notifications marked as read.");
    } catch (error) {
      toast.error("Could not mark notifications as read.");
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="notifications-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Bell className="h-4.5 w-4.5 h-5 w-5 text-primary" /> Notifications
          {unread > 0 && (
            <span
              data-testid="notifications-unread-badge"
              className="ml-1 rounded-full bg-primary/15 border border-primary/30 px-2 py-0.5 text-xs font-medium text-primary"
            >
              {unread} new
            </span>
          )}
        </CardTitle>
        {unread > 0 && (
          <Button size="sm" variant="ghost" onClick={markAllRead} data-testid="notifications-mark-read-button">
            <MailCheck className="h-3.5 w-3.5 mr-1.5" /> Mark all read
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {notifications.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="notifications-empty-state">
            No notifications yet. Membership, verification, and payment events will appear here.
          </p>
        ) : (
          <ScrollArea className="max-h-72">
            <ul className="space-y-2.5 pr-2">
              {notifications.slice(0, 30).map((notification, index) => {
                const Icon = KIND_ICONS[notification.kind] || Bell;
                return (
                  <motion.li
                    key={notification.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.3) }}
                    className={`flex items-start gap-3 rounded-lg border border-border p-3 ${
                      notification.is_read ? "bg-secondary/30" : "bg-primary/5 border-primary/20"
                    }`}
                  >
                    <Icon className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                    <div className="min-w-0">
                      <p className="text-sm leading-snug">{notification.message}</p>
                      <p className="mt-1 text-xs text-muted-foreground font-mono">
                        {new Date(notification.created_at).toLocaleString()}
                      </p>
                    </div>
                    {!notification.is_read && <span className="ml-auto mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  </motion.li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
